/**
 * Die Höhe der Anwendung und der Abstand für die Tastatur.
 *
 * **Zwei Zahlen, ein Beobachter.** Beide hängen an derselben Messung, und
 * getrennt würden sie auseinanderlaufen: `--app-hoehe` ist, wie hoch die
 * Anwendung sein soll, `--tastatur-hoehe`, wie viel davon unten frei bleibt.
 *
 * **Datei und Funktion heißen trotzdem weiter nach der Tastatur.** Der Name
 * ist damit zu eng, und das ist eine bewusste Entscheidung: hier steht die
 * ganze Vorgeschichte des Tastaturabstands, samt eines verworfenen ersten
 * Anlaufs, und ein `git mv` schnitte sie von ihrer Herkunft ab. Der Bruch
 * steht dafür hier erklärt — genauso wie beim Formular am Highlight.
 *
 * # `--app-hoehe`: die Höhe wird gemessen, nicht behauptet
 *
 * Aus dem Betrieb, zwei Bildschirmfotos: auf einem iPhone 17 Pro Max blieben
 * unter der Eingabezeile 60 Punkte in `--page` frei -- der Farbe des
 * *Dokuments*, nicht der der Leiste. Der Streifen lag also außerhalb der
 * Anwendung. Auf einem iPad war es das Gegenteil: die Eingabezeile lag
 * *unter* dem Fensterrand und ließ sich nicht heranholen.
 *
 * Davor stand dort `100dvh`, und genau dafür war es eingebaut worden. Einmal
 * zu kurz, einmal zu lang: **`dvh` ist auf diesen Geräten nicht das, was man
 * sieht.** Welche Zahl der Browser dort einsetzt, ließ sich von außen nicht
 * feststellen -- und noch einmal zu raten wäre der dritte Anlauf mit
 * derselben Methode gewesen.
 *
 * Also `window.innerHeight`, gemessen und nachgeführt. Und ausdrücklich
 * **nicht** `visualViewport.height`: die schrumpft unter iOS mit der
 * Tastatur, und genau daran ist der erste Anlauf gescheitert (siehe unten).
 * `innerHeight` tut das dort nicht. Wo ein Browser das Fenster von sich aus
 * verkleinert (Android), schrumpft `innerHeight` mit -- dort ist der
 * Tastaturabstand ohnehin 0, es wird also nichts doppelt gezählt.
 *
 * # `--tastatur-hoehe`: der Abstand
 *
 * Aus dem Betrieb, auf dem iPhone: Chat öffnen, ins Feld tippen — und das Feld
 * liegt unter der Tastatur. Auf dem iPad passt es.
 *
 * Der Grund ist ein alter Unterschied in iOS-Safari: die Tastatur verkleinert
 * dort **nicht das Fenster**, sondern legt sich darüber. `height: 100%` misst
 * weiter die volle Höhe, die Anwendung reicht also hinter die Tastatur, und
 * was unten sitzt — hier die Eingabezeile — bleibt darunter liegen.
 *
 * Gemessen wird deshalb nicht die Höhe, sondern die **Überdeckung**: was das
 * Fenster hat und die sichtbare Fläche nicht. Genau dieser Betrag steht als
 * `--tastatur-hoehe` bereit, und die Anwendung lässt ihn unten frei.
 *
 * Der erste Anlauf hat stattdessen die Höhe von `html`, `body` und `#root` auf
 * die sichtbare Fläche gesetzt — und damit einen schlimmeren Fehler gebaut:
 * eine Anwendung, die kleiner ist als das Fenster, liegt irgendwo darin, und
 * wo, entscheidet iOS. Schiebt Safari die Fläche, um das Eingabefeld
 * freizustellen, rutscht sie aus dem Blick. Auf dem Gerät war dann gar nichts
 * mehr zu sehen, nur noch die Tastatur. Ein Abstand kann das nicht: die
 * Anwendung bleibt so groß wie das Fenster und an ihrem Platz. Stimmt der
 * Betrag einmal nicht, steht man da, wo man vorher stand — mehr nicht.
 *
 * Nicht genommen wurden zwei naheliegende Wege: `100dvh` kennt die Tastatur
 * nicht, nur die Browserleisten, und `interactive-widget=resizes-content` im
 * Viewport-Meta wäre ein zweiter Mechanismus neben diesem, dessen Rückhalt in
 * Safari offen ist — zwei, die sich in die Quere kommen können, sind schlechter
 * als einer.
 */

/**
 * Darunter ist es eine Browserleiste und keine Tastatur.
 *
 * **Die Schwelle allein reicht nicht**, und das kam aus dem Betrieb: unter der
 * Eingabezeile blieb ein Streifen leer. Eine überlagernde Browserleiste macht
 * dieselbe Differenz wie eine kleine Tastatur; die Zahl trennt die Fälle
 * nicht. Sie bleibt trotzdem stehen, gegen Ausreißer in der Messung.
 */
const SCHWELLE = 80

/**
 * Steht der Cursor wirklich in einem Schreibfeld?
 *
 * **Ohne Fokus keine Tastatur.** Das ist kein Schwellenwert, sondern die
 * Sache selbst -- und der einzige Weg, eine Tastatur von einer Browserleiste
 * zu unterscheiden, die sich über die Seite legt.
 */
function schreibtJemand(): boolean {
  const el = document.activeElement
  if (!(el instanceof HTMLElement)) return false
  return (
    el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el.isContentEditable
  )
}

/**
 * Darunter wird nichts gesetzt.
 *
 * Ein Ausreißer während einer Drehung darf die Anwendung nicht zusammenfalten
 * -- dieselbe Haltung wie der Deckel beim Tastaturabstand. Kein Fenster, in
 * dem diese Anwendung sinnvoll läuft, ist 200 Punkte hoch.
 *
 * **Hieß einmal `BODEN`, und das war ein zweites Wort für eine andere Sache:**
 * die Zahl aus `vollbildHoehe()` heißt jetzt Mindesthöhe, und zwei Bedeutungen
 * für ein Wort sind genau das, was die Sprachregeln hier vermeiden sollen.
 * Diese Zahl ist keine Mindesthöhe, sondern eine Verwerfungsgrenze.
 */
const UNSINNIG_KLEIN = 200

/**
 * Läuft das hier als Web-App vom Home-Bildschirm?
 *
 * **`navigator.standalone` allein reicht nicht**, und das kam aus dem Betrieb:
 * mit der Mindesthöhe aus `d4b4176` stand auf dem iPhone weiterhin
 * `Gesetzte Höhe: 812px` bei `Bildschirm: 402 × 874`, auf dem iPad `1000` bei
 * `1032` -- sie griff auf keinem der beiden Geräte.
 *
 * Der Grund ist Apples Vorläufer selbst: `navigator.standalone` ist nie
 * standardisiert worden und **auf iPadOS gar nicht vorhanden**. Eine Prüfung
 * darauf schließt also genau das Gerät aus, auf dem der Fehler zuerst gemeldet
 * wurde.
 *
 * Der Standard dafür ist die Medienabfrage `display-mode`. Sie steht zuerst;
 * `navigator.standalone` bleibt als Rückfall für ältere Safari-Fassungen
 * daneben. Abgefragt werden alle drei Betriebsarten, die kein Browser-Tab
 * sind -- die Anzeigeart steht im `manifest.webmanifest` und kann sich ändern,
 * ohne dass jemand hier nachsieht.
 */
function alsApp(): boolean {
  const frage = window.matchMedia?.bind(window)
  if (frage !== undefined) {
    for (const art of ['standalone', 'fullscreen', 'minimal-ui']) {
      if (frage(`(display-mode: ${art})`).matches) return true
    }
  }
  // Apples Vorläufer: auf dem iPhone seit jeher, auf dem iPad nie.
  return (navigator as unknown as { standalone?: boolean }).standalone === true
}

/**
 * Die sichtbare Fläche — geprüft, nicht geglaubt.
 *
 * **Aus dem Betrieb, mit Mitschrieb:** im Chat stand bei offener Tastatur ein
 * breiter Zwischenraum zwischen Eingabezeile und Tastatur, und in *jeder*
 * Zeile des Mitschriebs stand `sichtbar = −33554432`. iOS meldete für
 * `visualViewport.height` einen Müllwert (−2²⁵) — dauerhaft, über
 * Seitenwechsel hinweg, mutmaßlich seit die Tastatur einmal ohne Fokusverlust
 * verschwand (#31). Kein Leser prüfte den Wert, und ein Wert machte drei
 * Symptome: der Abstand lief in seinen Deckel (die halbe Anwendung frei),
 * jede Tastatur galt als Bildschirmtastatur, und mit so viel Reserve ließ
 * sich die Überschrift aus dem Bild schieben.
 *
 * `--app-hoehe` hat seine Verwerfungsgrenze längst (`UNSINNIG_KLEIN`); die
 * sichtbare Fläche hatte keine. Das ist dieselbe Haltung: ein unmöglicher
 * Messwert wird **verworfen, nicht verrechnet.** Unmöglich heißt hier: nicht
 * endlich, nicht positiv, höher als die lange Bildschirmseite (plus
 * Schwelle), oder weiter verschoben, als der Bildschirm überhaupt reicht.
 *
 * **Verworfen heißt: das Fenster übernimmt.** Der erste Rückfall war
 * „Abstand 0, iOS stellt schon frei" -- und der Mitschrieb vom Gerät hat ihn
 * widerlegt: iOS' Freistellen ist im verkeilten Zustand selbst kaputt, die
 * Seite sprang zwischen verschoben und nicht verschoben, und dazwischen lag
 * das Eingabefeld unter der Tastatur, bis das Tippen es hervorholte.
 * Derselbe Mitschrieb zeigt aber auch, was ehrlich geblieben ist:
 * `innerHeight` ging sauber 874 → 472 → 874 mit der Tastatur mit, während
 * die sichtbare Fläche Müll meldete. In der Web-App schrumpft das Fenster
 * mit der Bildschirmtastatur -- also trägt es beide Antworten auch allein:
 * der Überstand der Anwendung unter der Fensterkante ist das Verdeckte, und
 * ein Fenster deutlich unter der Bildschirmhöhe heißt Bildschirmtastatur.
 */
function sichtMasse(): { hoehe: number, versatz: number } | null {
  const sicht = window.visualViewport
  if (!sicht) return null
  const hoehe = sicht.height
  const versatz = sicht.offsetTop
  if (!Number.isFinite(hoehe) || !Number.isFinite(versatz)) return null
  const lang = Math.max(window.screen.width, window.screen.height)
  if (hoehe <= 0 || hoehe > lang + SCHWELLE) return null
  if (Math.abs(versatz) > lang) return null
  return { hoehe, versatz }
}

/**
 * Verwirft die Grenze gerade — obwohl es `visualViewport` gibt?
 *
 * Für die Diagnosekarte: der Rohwert bleibt dort sichtbar (genau er hat
 * diesen Fehler gefunden), und daneben steht, dass er nicht verrechnet wird.
 */
export function sichtVerworfen(): boolean {
  return window.visualViewport != null && sichtMasse() === null
}

/**
 * Liegt gerade eine **Bildschirmtastatur** über der Anwendung?
 *
 * **Die Frage ist nicht, ob ein Feld den Fokus hat, sondern womit geschrieben
 * wird.** Aus dem Betrieb (#28): „HW-Tastatur = Senden, Bildschirmtastatur =
 * Leerzeile" -- wie in iMessage und WhatsApp. Dafür braucht es ein Merkmal,
 * das beide Fälle trennt, und ein Merkmal des *Bildschirms* taugt dafür nicht:
 * ein iPad mit angeschlossener Tastatur meldet einen groben Zeiger, und daran
 * ist ein früherer Anlauf gescheitert.
 *
 * **Der erste Anlauf las den freigehaltenen Abstand, und das war falsch.** Aus
 * dem Betrieb: „Bildschirmtastatur: sendet auch -- hat 1x funktioniert, aber
 * nicht reproduzierbar." Der Abstand beantwortet eine andere Frage, nämlich
 * *wie weit die Tastatur die Anwendung überlagert* -- und das ist 0, sobald
 * iOS die Seite selbst verschoben hat (Mitschrieb: `478 / 478 / 812 / 478 ·
 * 334↕334`, Abstand 0 bei offener Tastatur). Das „1x funktioniert" waren die
 * Augenblicke, in denen er gerade nicht 0 war.
 *
 * Gefragt wird deshalb nach der **sichtbaren Fläche gegen den Bildschirm**:
 * steht eine Tastatur davor, bleibt von 874 Punkten noch 478.
 *
 * **Was das auf Android bedeutet, gehört gesagt:** dort verkleinert der
 * Browser das Fenster selbst, der Abstand bleibt 0, und die Eingabetaste
 * sendet wie am Rechner. Das ist nicht die Regel aus der Meldung, aber es ist
 * das bisherige Verhalten -- und ein zweites Merkmal zu erfinden, das keiner
 * nachmessen kann, wäre schlechter als eine benannte Grenze.
 */
export function bildschirmtastaturVerdeckt(): boolean {
  // Durch die Verwerfungsgrenze: ein Müllwert wie −33554432 wäre hier immer
  // „kleiner als der Bildschirm" gewesen -- und jede Tastatur eine
  // Bildschirmtastatur.
  const sicht = sichtMasse()
  if (sicht === null) {
    // Verworfen oder gar nicht vorhanden: das Fenster übernimmt. In der
    // Web-App schrumpft es mit der Bildschirmtastatur (der Mitschrieb des
    // verkeilten Zustands: 874 → 472 → 874, während die Fläche Müll
    // meldete); im Browser-Tab schrumpft es nicht, und dann bleibt es bei
    // der benannten Grenze -- die Eingabetaste sendet.
    const voll = vollbildHoehe()
    return voll !== null && window.innerHeight < voll - SCHWELLE
  }

  // **In der Web-App ist der Bildschirm die Bezugsgröße.** Steht eine
  // Bildschirmtastatur davor, bleibt von 874 Punkten noch 478 sichtbar; ohne
  // sie ist beides gleich. Das gilt auch dann, wenn iOS die Seite schon
  // verschoben hat -- und genau daran ist der erste Anlauf gescheitert.
  const voll = vollbildHoehe()
  if (voll !== null) return sicht.hoehe < voll - SCHWELLE

  // Sonst -- im Browser-Tab -- deckt die Tastatur die Seite ab, ohne das
  // Fenster zu verkleinern: dann ist die Differenz die Tastatur. Am Rechner
  // sind beide gleich, und auf Android schrumpft das Fenster mit; beide Male
  // kommt 0 heraus, und die Eingabetaste sendet. Das ist die benannte Grenze.
  return window.innerHeight - sicht.hoehe > SCHWELLE
}

/**
 * Was die Mindesthöhe gerade entscheidet -- für die Diagnosekarte.
 *
 * **Damit die nächste Runde nicht wieder aus einem Bildschirmfoto gerechnet
 * wird.** Zwei Anläufe sind daran vorbeigegangen: sie griff auf dem
 * Gerät nicht, und aus den angezeigten Zahlen war nicht zu sehen, an welcher
 * der beiden Bedingungen es lag. Jetzt steht beides da.
 */
export function bodenLage(): { alsApp: boolean, breite: number, boden: number | null } {
  return { alsApp: alsApp(), breite: Math.round(window.innerWidth), boden: vollbildHoehe() }
}

/**
 * Wie hoch das Fenster im Vollbild sein *muss* -- oder `null`.
 *
 * **Weil iOS die Fensterhöhe verliert und sie nicht wiederfindet.** Aus dem
 * Betrieb, zwei Mitschriebe desselben Standes, jeweils Tastatur auf und wieder
 * zu:
 *
 * ```
 * iPhone 16 Pro   874 → 478 (Tastatur) → 812   und bleibt bei 812
 * iPad quer      1032 → 534 (Tastatur) → 1000  und bleibt bei 1000
 * ```
 *
 * 874 − 62 = 812, 1032 − 32 = 1000, und 62 beziehungsweise 32 ist der obere
 * Sicherheitsabstand des Geräts. **Es fehlt genau der Streifen unter der
 * Statusleiste**, und zwar auf Dauer: über Seitenwechsel hinweg, bis die
 * Anwendung neu startet. Sichtbar wird er unten, weil die Anwendung oben
 * angeschlagen bleibt.
 *
 * `100dvh` hilft dagegen nicht: es meldet dieselbe falsche Zahl (812 und 1000
 * in denselben Mitschrieben). `max(100dvh, --app-hoehe)` nimmt damit das
 * Größere von zwei falschen Werten. **`window.screen` ist der einzige, der
 * richtig bleibt** -- also stammt die Mindesthöhe von dort.
 *
 * Zwei Bedingungen, und beide sind nötig:
 *
 * 1. **Nur in der Web-App vom Home-Bildschirm.** In einem Browser-Tab ist ein
 *    kleineres Fenster richtig -- dort stehen Leisten davor, und ein
 *    erzwungenes Vollbild schöbe die Eingabezeile darunter.
 * 2. **Nur, wenn die Breite beweist, dass wir das ganze Bild haben.** `screen`
 *    meldet auf iPadOS immer die Hochkantmaße, egal wie das Gerät liegt. Passt
 *    `innerWidth` auf eine der beiden Seiten, ist es Vollbild und die andere
 *    Seite ist die Höhe. Im geteilten Fenster passt keine -- dann gilt, was der
 *    Browser sagt.
 *
 * Dass die Mindesthöhe auch bei offener Tastatur die richtige Zahl wäre, ist kein
 * Zufall: `--app-hoehe` trägt die *ganze* Fensterhöhe, den Platz für die
 * Tastatur nimmt `--tastatur-hoehe` weg. Zum Zuge kommt er dort trotzdem
 * nicht -- `hoeheNachmessen()` steigt vorher aus, solange jemand schreibt.
 *
 * **Wie erkannt wird, dass es die Web-App ist**, steht in `alsApp()` -- und
 * daran ist der erste Anlauf gescheitert.
 *
 * **Das Wort ist „Mindesthöhe" und nicht „Boden".** Aus dem Betrieb: „was heißt
 * ‚Boden aus dem Bildschirm'? bitte sinnvolle deutsche Übersetzung." Berechtigt
 * -- „Boden" war ein Bild aus dem Englischen (*floor*) und benennt die Sache
 * nicht. Gemeint ist die Höhe, die der Bildschirm mindestens vorgibt; so heißt
 * es jetzt auch in der Diagnosekarte.
 */
export function vollbildHoehe(): number | null {
  if (!alsApp()) return null
  const lang = Math.max(window.screen.width, window.screen.height)
  const kurz = Math.min(window.screen.width, window.screen.height)
  if (window.innerWidth === lang) return kurz
  if (window.innerWidth === kurz) return lang
  return null
}

/**
 * Die Höhe der Anwendung neu messen.
 *
 * **Nicht, während jemand schreibt.** Das ist der Kern, und er hat einen
 * Fehler gekostet: aus dem Betrieb, mit einer Reihenfolge, die ihn festnagelt
 * -- „Neustart der WebApp: alles ok; kein Rand unten im Chat. Aufruf anderer
 * Menüs (Einstellungen, Tastaturtest …) — danach ist im Chat und in der Karte
 * unten ein Rand."
 *
 * In einer Web-App vom Home-Bildschirm verkleinert iOS beim Öffnen der
 * Tastatur das **Fenster**. `innerHeight` ist dann nicht mehr die Höhe der
 * Anwendung, sondern die des Restes daneben -- und wer diesen Wert festhält,
 * hält den Fehler fest. Die Karte *Einstellungen → Tastatur* fordert zum
 * Tippen auf und ist damit der zuverlässigste Weg, ihn auszulösen; genau
 * deshalb stand sie in der Meldung.
 *
 * Der Tastaturabstand eine Ebene tiefer stellt dieselbe Frage seit jeher
 * (`schreibtJemand()`). Sie hier nicht zu stellen war die Lücke.
 *
 * **Ohne Fokus keine Tastatur** -- und `focusout` löst die Messung ohnehin
 * gleich wieder aus.
 */
export function hoeheNachmessen(anlass = 'messen'): void {
  merken(anlass)
  const tippt = schreibtJemand()
  if (!tippt) seiteZurueck()
  // **Beim Schreiben gilt das Fenster, sonst die Mindesthöhe.**
  //
  // Aus dem Betrieb, mit dem Mitschrieb aus `a45a0cb`, Tastatur offen:
  //
  //     730 / 478 / 874 / 500 · 374↕334
  //     730 / 478 / 874 / 248 · 374↕334
  //
  // Fenster 730, Anwendung 874, Seite 374. Die Mindesthöhe hält die Anwendung
  // 144 Punkte höher als das Fenster -- und **erst dadurch gibt es überhaupt
  // etwas zu scrollen.** iOS schiebt die Seite hoch, um das Feld freizustellen,
  // die Unterkante landet bei 248, und die Tastatur beginnt viel tiefer:
  // dazwischen stand der breite Streifen aus der Meldung.
  //
  // Solange jemand schreibt, ist deshalb das Fenster die richtige Zahl: dann
  // ist die Anwendung genau so hoch wie das, worin sie liegt, es gibt nichts zu
  // verschieben, und den Platz für die Tastatur nimmt `--tastatur-hoehe`.
  //
  // **Der frühere Ausstieg ist damit hinfällig, aber sein Grund nicht.** Er kam
  // aus `b164f11`: eine Messung beim Schreiben hielt eine zu kleine Zahl fest
  // und behielt sie. Das kann jetzt nicht mehr bleiben -- beim Loslassen misst
  // `focusout` sofort neu, und dann gilt wieder die Mindesthöhe.
  //
  // **Und nur, wenn die Tastatur wirklich oben ist.** Der erste Anlauf hat die
  // Höhe schon beim bloßen Fokus dem Fenster angeglichen -- auf iOS sind das
  // 874 gegen 812, und die Anwendung sprang bei jedem Antippen eines Feldes um
  // 62 Punkte. Im Prüflauf traf danach der Anmeldeknopf nicht mehr: das
  // Loslassen verschob ihn zwischen Drücken und Lösen. Dieselbe Schwelle wie
  // beim Abstand trennt die Fälle -- ein verlorener Sicherheitsabstand ist
  // klein, eine Tastatur ist es nie.
  const mindest = vollbildHoehe() ?? 0
  const fenster = Math.round(window.innerHeight)
  if (tippt && fenster > mindest - SCHWELLE) return
  const hoch = tippt ? fenster : Math.max(fenster, mindest)
  // Ein Ausreißer während einer Drehung darf die Anwendung nicht
  // zusammenfalten.
  if (hoch < UNSINNIG_KLEIN) return
  document.documentElement.style.setProperty('--app-hoehe', `${hoch}px`)
  merken(`${anlass} → ${hoch}`)
}

/**
 * Die Seite selbst steht immer oben.
 *
 * **Aus dem Betrieb, an der Personenverwaltung:** „sobald die Tastatur
 * eingeblendet wird, sieht's komisch aus [...] und so, wenn die Tastatur wieder
 * weg ist — man kann auch nicht mehr ganz nach oben scrollen." Am
 * Bildschirmfoto ausgemessen: die Anwendung endete bei 655 von 874 Punkten,
 * darunter standen 219 Punkte in der Farbe des Dokuments -- und **die
 * Kopfzeile fehlte oben**. Die Anwendung war also nicht zu klein, sondern
 * nach oben geschoben.
 *
 * Der Grund ist iOS: um ein Eingabefeld über der Tastatur freizustellen,
 * scrollt es die **Seite**. Möglich ist das, weil die Anwendung so hoch ist wie
 * das ganze Fenster, das Fenster mit der Tastatur aber schrumpft -- damit gibt
 * es plötzlich etwas zu scrollen. Nach dem Schließen bleibt die Seite stehen,
 * wo sie steht.
 *
 * `body { overflow: hidden }` in `app.css` sagt genau das schon, und iOS hält
 * sich beim Freistellen nicht daran. Also wird es nachgezogen -- **aber nur,
 * wenn niemand schreibt**: währenddessen ist das Verschieben die Absicht des
 * Geräts, und dagegenzuhalten hieße, das Feld wieder unter die Tastatur zu
 * schieben.
 */
function seiteZurueck(): void {
  if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0)
}

/**
 * Ein Fahrtenschreiber für die Höhe.
 *
 * **Weil das Hinsehen den Fehler behebt.** Aus dem Betrieb kam ein
 * Bildschirmfoto mit dem Streifen und eine Minute später die Diagnosekarte mit
 * „874 von 874" -- alles in Ordnung. Die Karte misst `100dvh`, indem sie einen
 * Kasten ins Dokument hängt; das erzwingt einen Neuaufbau und räumt genau den
 * Zustand weg, den sie zeigen soll. Ein Messgerät, das man erst befragen muss,
 * kann diesen Fehler nicht sehen.
 *
 * Also wird mitgeschrieben, und zwar **nur aus freien Werten**: `innerHeight`,
 * die sichtbare Fläche, der Bildschirm und der Kasten der Anwendung. Keiner
 * davon hängt einen Kasten ins Dokument, keiner erzwingt etwas. `100dvh` steht
 * bewusst **nicht** darin -- es wäre wieder der Beobachter, der stört.
 *
 * Fünfundzwanzig Einträge reichen: was zwischen dem Chat und den
 * Einstellungen geschieht, sind eine Handvoll Ereignisse.
 */
export interface Hoehenstand {
  zeit: string
  anlass: string
  fenster: number
  sicht: number
  schirm: number
  shell: number | null
  /**
   * Wo die unterste Leiste endet — Eingabezeile oder Kartenleiste.
   *
   * **Sie trennt die beiden verbliebenen Erklärungen.** Aus dem Betrieb: auf
   * der Einstellungsseite reicht die Anwendung bis zur Unterkante, im Chat
   * bleiben 62 Punkte in der Farbe des Dokuments frei. Ist hier `shell` gleich
   * dem Fenster und diese Zahl kleiner, sitzt der Fehler **in der Seite**; ist
   * `shell` selbst kleiner, sitzt er im Gerüst darüber. Ohne diese Zahl bleibt
   * es beim Raten.
   */
  zeile: number | null
  /**
   * Wie weit die **Seite** verschoben ist, und wie weit die **Fläche**.
   *
   * **Sie trennen die beiden Erklärungen für „nach oben geschoben".** Aus dem
   * Betrieb, an der Personenverwaltung: nach dem Schließen der Tastatur endete
   * die Anwendung bei 655 von 874 Punkten, und oben fehlte die Kopfzeile --
   * derselbe Betrag. Verschoben war also etwas, nur was?
   *
   * `seite` ist `window.scrollY`: das Dokument. `versatz` ist
   * `visualViewport.offsetTop`: die sichtbare Fläche, die iOS über dem
   * Dokument verschiebt. Der erste Anlauf hat die Seite zurückgeholt und nichts
   * geändert -- ohne beide Zahlen bleibt der zweite wieder ein Raten.
   */
  seite: number
  versatz: number
  pfad: string
}

const VERLAUF: Hoehenstand[] = []
const VERLAUF_LAENGE = 25

function merken(anlass: string): void {
  const shell = document.querySelector('.shell')
  const stand: Hoehenstand = {
    zeit: new Date().toLocaleTimeString('de-DE'),
    // **Der Befund steht am Anlass, nicht als eigener Eintrag.** Die
    // Unterdrückung darunter vergleicht Werte und keinen Anlass -- ein
    // eigener Eintrag „Fläche verworfen" käme direkt nach dem Ereignis mit
    // denselben Zahlen und würde verschluckt. So trägt ihn der Eintrag, der
    // ohnehin geschrieben wird. Der Rohwert bleibt in `sicht` stehen.
    anlass: sichtVerworfen() ? `${anlass} · Fläche verworfen` : anlass,
    fenster: Math.round(window.innerHeight),
    sicht: Math.round(window.visualViewport?.height ?? 0),
    schirm: Math.round(window.screen.height),
    shell: shell === null ? null : Math.round(shell.getBoundingClientRect().height),
    zeile: (() => {
      const unten = document.querySelector('.composer, .karte__leiste, .scroll-panel')
      return unten === null ? null : Math.round(unten.getBoundingClientRect().bottom)
    })(),
    seite: Math.round(window.scrollY),
    versatz: Math.round(window.visualViewport?.offsetTop ?? 0),
    pfad: location.pathname,
  }

  // **Nur, wenn sich etwas geändert hat.** Sonst schiebt ein Scrollen die
  // interessanten Einträge aus dem Fenster, bevor jemand sie liest.
  const letzter = VERLAUF[0]
  if (
    letzter !== undefined
    && letzter.fenster === stand.fenster
    && letzter.sicht === stand.sicht
    && letzter.shell === stand.shell
    && letzter.zeile === stand.zeile
    && letzter.seite === stand.seite
    && letzter.versatz === stand.versatz
    && letzter.pfad === stand.pfad
  ) return

  VERLAUF.unshift(stand)
  if (VERLAUF.length > VERLAUF_LAENGE) VERLAUF.length = VERLAUF_LAENGE
}

/** Der Mitschrieb, neueste zuerst. */
export function hoehenVerlauf(): Hoehenstand[] {
  return VERLAUF.slice()
}

/**
 * Und ein Beobachter am Kasten der Anwendung selbst.
 *
 * Er meldet sich **genau dann, wenn der Streifen entsteht oder verschwindet**
 * -- ohne Umfrage und ohne Zeitgeber. Wird erst eingehängt, wenn `.shell`
 * dasteht; beim ersten Lauf gibt es sie noch nicht.
 */
function shellBeobachten(): void {
  if (typeof ResizeObserver === 'undefined') return
  const shell = document.querySelector('.shell')
  if (shell === null) {
    window.setTimeout(shellBeobachten, 500)
    return
  }
  new ResizeObserver(() => merken('Kasten der Anwendung')).observe(shell)
}

export function tastaturBeobachten(): void {
  // **Die Höhe zuerst, und ohne `visualViewport`.** Sie ist die wichtigere
  // der beiden Zahlen und darf nicht daran hängen, ob es die Schnittstelle
  // gibt -- sonst stünde auf einem Browser ohne sie wieder `100dvh` da.
  hoeheNachmessen('Start')
  shellBeobachten()

  const sicht = window.visualViewport

  /** Der Zeitgeber der Wache -- läuft nur, solange ein Abstand steht. */
  let wache: number | null = null

  /**
   * Den freigehaltenen Platz nachrechnen -- bei **jedem** Weckruf.
   *
   * Aus dem Betrieb, zwei Meldungen mit derselben Signatur: am iPad wurde bei
   * offener Bildschirmtastatur eine HW-Tastatur angeschlossen, am iPhone bei
   * offener Tastatur ein Bildschirmfoto gemacht. Beide Male verschwindet die
   * Tastatur, **ohne dass jemand das Feld loslässt** -- und beide Male blieb
   * ein grauer Bereich stehen, genau wo die Tastatur war.
   *
   * Der Abstand wurde bis dahin nur bei den Ereignissen der sichtbaren Fläche
   * und bei `focusin`/`focusout` nachgerechnet. Meldet sich das Verschwinden
   * der Tastatur nur auf Fensterebene, blieb der alte Wert stehen -- die
   * Formel hätte längst 0 ergeben, sie wurde nur nie gefragt. Deshalb hängt
   * die Rechnung jetzt an denselben Weckrufen wie die Höhe.
   */
  const abstandNachziehen = () => {
    if (!sicht) return
    // Durch die Verwerfungsgrenze: mit `height = −33554432` lief die Rechnung
    // unten auf 33,5 Millionen und der Deckel hielt die halbe Anwendung frei
    // -- der breite Zwischenraum aus der Meldung.
    //
    // **Ist die Fläche verworfen, misst das Fenster.** Der erste Rückfall war
    // Abstand 0 -- und dann lag das Feld unter der Tastatur, bis das Tippen
    // es hervorholte, denn iOS' Freistellen ist im verkeilten Zustand selbst
    // kaputt. `innerHeight` blieb dort ehrlich (874 → 472 → 874); der
    // Überstand der Anwendung unter der Fensterkante ist das Verdeckte.
    // Wo das Fenster nicht schrumpft (Browser-Tab, Rechner) oder die
    // Anwendung mitschrumpft (Android), kommt 0 heraus -- wie bisher.
    const masse = sichtMasse()
    // **Wie weit die Anwendung unter die sichtbare Fläche ragt** -- gemessen,
    // nicht aus dem Fenster gerechnet.
    //
    // Hier stand `window.innerHeight - sicht.height`, mit der Begründung, die
    // Differenz sei genau die Tastatur. Auf iOS ist sie das nicht: das Fenster
    // ist dort **nicht** die Höhe der Anwendung. Aus dem Betrieb, Mitschrieb
    // auf der Personenseite bei offener Tastatur:
    //
    //     730 / 478 / 812 / 438 · 374↕334
    //
    // Fenster 730, Anwendung 812. Der alte Ausdruck ergibt 252, gebraucht wird
    // aber der Überstand der **Anwendung** über die sichtbare Fläche -- und
    // iOS hat die Seite obendrein schon um 374 verschoben, um das Feld
    // freizustellen. Wer beides addiert, schiebt den Inhalt zweimal hoch:
    // genau der breite Streifen aus der Meldung.
    //
    // Beides steckt in einer einzigen Messung: die Unterkante der Anwendung im
    // Bild gegen die Unterkante des Sichtbaren. Hat iOS schon genug
    // verschoben, kommt 0 heraus; überlagert die Tastatur die Anwendung, kommt
    // genau die Überlagerung heraus. Der frühere Ausdruck ist der Sonderfall
    // davon, in dem nichts verschoben ist.
    const unten = document.documentElement.getBoundingClientRect().bottom
    const roh = masse !== null
      ? unten - (masse.versatz + masse.hoehe)
      : unten - window.innerHeight

    // Nach oben begrenzt, damit ein Ausreißer in der Messung die Anwendung
    // nicht zusammenfalten kann. Der Abstand ist eine Hilfe, keine Gewalt.
    //
    // **Die Hälfte der Anwendung, nicht die des Fensters.** Auf iOS ist das
    // Fenster kleiner als die Anwendung (730 gegen 812); ein Deckel aus dem
    // Fenster schnitte den nötigen Abstand ab, bevor er wirken kann.
    const deckel = unten / 2
    const abstand = roh < SCHWELLE || !schreibtJemand()
      ? 0
      : Math.min(Math.round(roh), Math.round(deckel))

    document.documentElement.style.setProperty('--tastatur-hoehe', `${abstand}px`)

    /**
     * Die Wache: solange unten etwas freigehalten wird, wird nachgeprüft.
     *
     * Aus dem Betrieb (#31), zum wiederholten Mal und diesmal ohne Ausweg
     * über die Ereignisse: Tastatur eingeblendet, Bildschirmfoto erstellt --
     * die Tastatur verschwindet, der graue Balken bleibt. Der Fokus bleibt
     * dabei im Feld, `focusout` kommt nie, und iOS feuert in diesem Moment
     * **gar kein Ereignis**: kein `resize`, nichts an der sichtbaren Fläche.
     * Alle Nachmessungen hängen aber an Weckrufen -- die Formel hätte längst
     * 0 ergeben, es fragte sie nur wieder niemand. Dasselbe Loch wie damals
     * bei den Fensterereignissen, eine Ebene tiefer.
     *
     * Deshalb: ein freigehaltener Abstand ist ein Ausnahmezustand, und nur
     * in ihm läuft ein Zeitgeber -- einmal je Sekunde nachmessen, bis der
     * Abstand 0 ist, dann endet die Wache von selbst. Im Normalbetrieb (kein
     * Abstand) läuft nichts. Der Mitschrieb bleibt sauber, weil `merken`
     * unveränderte Stände ohnehin unterdrückt.
     */
    if (abstand > 0 && wache === null) {
      wache = window.setInterval(() => {
        hoeheNachmessen('Wache')
        abstandNachziehen()
      }, 1000)
    } else if (abstand === 0 && wache !== null) {
      window.clearInterval(wache)
      wache = null
    }
  }

  const messen = (anlass: string) => () => {
    hoeheNachmessen(anlass)
    abstandNachziehen()
  }

  window.addEventListener('resize', messen('Fenster verändert'))
  // Beim Drehen meldet Safari die neue Höhe erst nach dem Ereignis; ein
  // `resize` kommt zwar hinterher, aber nicht überall verlässlich.
  window.addEventListener('orientationchange', messen('gedreht'))
  // **Zurück aus dem Hintergrund heißt: neu messen.** Was zwischendurch am
  // Fenster geschehen ist, hat diese Seite nicht mitbekommen.
  window.addEventListener('pageshow', messen('Seite gezeigt'))
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) messen('zurück im Vordergrund')()
  })

  if (!sicht) {
    // Ohne die Schnittstelle bleibt es beim bloßen Höhensetzen: kein Abstand.
    return
  }

  const anpassen = messen('sichtbare Fläche')
  anpassen()
  sicht.addEventListener('resize', anpassen)
  sicht.addEventListener('scroll', anpassen)
  // **Beim Verlassen sofort messen.** Die sichtbare Fläche wächst danach zwar
  // ohnehin und löst ein `resize` aus -- aber erst, wenn die Tastatur
  // heruntergefahren ist. Bis dahin stünde der Abstand noch da, obwohl längst
  // niemand mehr schreibt.
  //
  // `focusin` ebenso: wer aus einem Feld in ein anderes wechselt, hat
  // durchgehend eine Tastatur, und ohne dieses Ereignis fiele der Abstand
  // dazwischen kurz weg.
  window.addEventListener('focusin', anpassen)
  window.addEventListener('focusout', anpassen)
}
